import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  BookOpen, CheckCircle, Clock, AlertCircle, Settings, GraduationCap,
  Loader2, Play, ArrowRight, FileEdit, Sparkles, TrendingUp, ArrowLeft, PlusCircle,
  X, Upload, ExternalLink
} from 'lucide-react';
import RequestCourseModal from '../../components/RequestCourseModal';
import AssignmentSubmission from '../../components/dashboard/AssignmentSubmission';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../context/ToastContext';

export default function DashboardOverview() {
  const { currentUser, userData, isProfileComplete } = useAuth();

  const { addToast } = useToast();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [pendingCourses, setPendingCourses] = useState([]);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [pendingAssignmentsCount, setPendingAssignmentsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [submissionModalData, setSubmissionModalData] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    async function initDashboard() {
      try {
        setLoading(true);
        // 1. Fetch Basic Enrollments
        const enrollQuery = query(collection(db, 'enrollments'), where('userId', '==', currentUser.uid));
        const enrollSnap = await getDocs(enrollQuery);
        const enrollments = enrollSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const enrolledIds = enrollments.map(e => e.courseId);

        // 2. Fetch Pending Registration Requests
        const regQuery = query(collection(db, 'registrations'), where('userId', '==', currentUser.uid));
        const regSnap = await getDocs(regQuery);
        const pendingIds = regSnap.docs
          .map(d => d.data())
          .filter(r => r.status !== 'customer' && !enrolledIds.includes(r.courseId))
          .map(r => r.courseId);

        // 3. Fetch All Relevant Course Docs
        const allTargetIds = [...new Set([...enrolledIds, ...pendingIds])];
        let allCourseData = [];
        if (allTargetIds.length > 0) {
          const promises = allTargetIds.map(async cid => {
            const snap = await getDoc(doc(db, 'courses', cid));
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
          });
          allCourseData = (await Promise.all(promises)).filter(Boolean);
        }

        // 4. Fetch Submissions for Grade/Pending checks
        const submissionsSnap = await getDocs(query(collection(db, 'submissions'), where('studentId', '==', currentUser.uid)));
        const studentSubmissions = submissionsSnap.docs.map(d => d.data());

        // 5. Categorize Courses & Extract Pending Assignments
        const enrolledWithData = [];
        const pendingList = [];
        const unsubmittedList = [];

        allCourseData.forEach(course => {
          if (enrolledIds.includes(course.id)) {
            enrolledWithData.push({
              ...course,
              enrollmentData: enrollments.find(e => e.courseId === course.id)
            });

            // Check curriculum for assignments
            if (course.curriculum) {
              course.curriculum.forEach((unit, ui) => {
                unit.lessons?.forEach((lesson, li) => {
                  if (lesson.type === 'assignment' || lesson.type === 'final_project') {
                    const assignmentId = `u${ui}-l${li}`;
                    const submission = studentSubmissions.find(s => s.courseId === course.id && s.assignmentId === assignmentId);
                    if (!submission || submission.status === 'needs_revision') {
                      unsubmittedList.push({
                        ...lesson,
                        id: assignmentId,
                        unitIdx: ui,
                        lessonIdx: li,
                        courseId: course.id,
                        courseTitle: course.title,
                        unitTitle: unit.title,
                        submission: submission || null
                      });
                    }
                  }
                });
              });
            }
          } else if (pendingIds.includes(course.id)) {
            pendingList.push(course);
          }
        });

        setEnrolledCourses(enrolledWithData);
        setPendingCourses(pendingList);
        setPendingAssignments(unsubmittedList);
        setPendingAssignmentsCount(unsubmittedList.length);

      } catch (error) {
        console.error("Dashboard Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    }
    initDashboard();
  }, [currentUser]);

  const totalCourses = enrolledCourses.length;

  const stats = [
    {
      label: 'الدورات المسجّل بها',
      value: totalCourses.toString(),
      icon: GraduationCap,
      id: 'enrolled',
      gradient: 'from-slate-900 to-slate-800 dark:from-zinc-900 dark:to-black',
      glow: 'bg-primary-500/20'
    },
    {
      label: 'طلبات الانضمام المراجعة',
      value: pendingCourses.length.toString(),
      icon: Clock,
      id: 'pending',
      bg: 'bg-white dark:bg-zinc-900',
      glow: 'bg-amber-500/10'
    },
    {
      label: 'التكاليف المعلقة',
      value: pendingAssignmentsCount.toString(),
      icon: FileEdit,
      id: 'assignments',
      link: '/dashboard/assignments',
      bg: 'bg-white dark:bg-zinc-900',
      glow: pendingAssignmentsCount > 0 ? 'bg-rose-500/10' : 'bg-emerald-500/10'
    },
    {
      label: 'حالة الحساب',
      value: isProfileComplete ? 'مكتمل' : 'غير مكتمل',
      icon: isProfileComplete ? CheckCircle : AlertCircle,
      id: 'status',
      bg: 'bg-white dark:bg-zinc-900',
      glow: isProfileComplete ? 'bg-emerald-500/10' : 'bg-rose-500/10'
    },
  ];

  const greetingTime = new Date().getHours();
  const greeting = greetingTime < 12 ? 'صباح الخير' : greetingTime < 17 ? 'مساء الخير' : 'مساء النور';

  const isFirstTime = enrolledCourses.length === 0 && pendingCourses.length === 0 && !isProfileComplete;

  const getGreetingMessage = () => {
    if (isFirstTime) {
      return "خطوتك الأولى تبدأ من هنا! أكمل ملفك الشخصي لتفتح أمامك آفاقاً لا حصر لها من المعرفة.";
    }
    if (enrolledCourses.length > 0) {
      return "نحن فخورون بشغفك المستمر. استأنف دروسك الآن وأطلق العنان لإبداعك لتصل إلى القمة.";
    }
    return "مكتبتك الفنية بانتظارك لتملأها بالإبداع. بادر بالانضمام للبرامج التي تناسب طموحك.";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

      {/* ── Cinematic Welcome Banner ────────────────────────────── */}
      <div className="relative bg-gradient-to-bl from-slate-900 via-slate-800 to-primary-900/80 dark:from-[#0a0a0a] dark:via-zinc-900 dark:to-primary-950/60 rounded-3xl p-8 md:p-10 overflow-hidden shadow-2xl group border border-white/5">
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-500/20 rounded-full blur-[100px] -translate-x-1/3 -translate-y-1/3 group-hover:scale-125 transition-transform duration-1000 pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-56 h-56 bg-primary-600/15 rounded-full blur-[80px] translate-x-1/3 translate-y-1/3 pointer-events-none"></div>
        <div className="absolute top-4 right-6 opacity-10 text-[10rem] font-black text-white pointer-events-none select-none leading-none">لُباب</div>

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Avatar Section */}
            <div className="relative shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl border-4 border-white/10 dark:border-white/5 shadow-2xl overflow-hidden bg-white/5 flex items-center justify-center relative backdrop-blur-md">
                {userData?.photoURL ? (
                  <img src={userData.photoURL} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-black text-white/20">{(userData?.nameAr || userData?.name || 'ط').charAt(0)}</span>
                )}
              </div>
            </div>

            <div className="text-center md:text-right">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                <Sparkles className="w-5 h-5 text-primary-400 animate-pulse" />
                <span className="text-primary-400 font-bold text-sm tracking-wider uppercase">{greeting} 👋</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-black text-white mb-2 leading-tight">
                أهلاً بك، <span className="text-transparent bg-clip-text bg-gradient-to-l from-primary-300 to-primary-500">{userData?.nameAr || userData?.name}</span>!
              </h1>
              <p className="text-slate-300/80 font-medium text-lg max-w-xl">
                {getGreetingMessage()}
              </p>
            </div>
          </div>

          <Link to="/courses" className="bg-primary-600 hover:bg-primary-500 text-white px-8 py-4 rounded-xl font-black flex items-center gap-2 shadow-lg shadow-primary-500/30 transition-all hover:-translate-y-1 hover:shadow-xl shrink-0">
            <Play className="w-5 h-5 ml-1" />
            استكشف الدورات
          </Link>
        </div>
      </div>

      {/* ── Statistics Area ────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const isGradient = !!stat.gradient;
          return (
            <Link
              key={stat.id}
              to={stat.link || '#'}
              className={`${isGradient ? stat.gradient + ' text-white border-white/5' : stat.bg + ' border-slate-200 dark:border-white/10'} rounded-3xl p-6 shadow-xl relative overflow-hidden group border transition-all duration-500 hover:scale-[1.02]`}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 ${stat.glow} blur-2xl rounded-full translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-700`}></div>
              <div className="relative z-10 flex items-center gap-5">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-md ${isGradient ? 'bg-white/10' : 'bg-slate-50 dark:bg-white/5 order border-slate-100 dark:border-white/5'}`}>
                  <Icon className={`w-7 h-7 ${isGradient ? 'text-primary-300' : 'text-primary-600 dark:text-primary-400'}`} />
                </div>
                <div>
                  <p className={`text-sm font-bold ${isGradient ? 'text-slate-300' : 'text-slate-500 dark:text-zinc-400'}`}>{stat.label}</p>
                  <p className="text-2xl font-black">{stat.value}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* ── Profile Completion Gate ────────────────────────────── */}
      {!isProfileComplete && (
        <div className="bg-gradient-to-r from-rose-500/10 to-transparent border border-rose-500/20 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/5 rounded-full blur-[80px] pointer-events-none"></div>
          <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center shrink-0 border border-rose-500/30">
            <AlertCircle className="w-8 h-8 text-rose-500" />
          </div>
          <div className="flex-1 text-center md:text-right">
            <h3 className="text-xl font-black text-slate-900 dark:text-white mb-1">أكمل ملفك الشخصي لتفعيل كافة المميزات!</h3>
            <p className="text-slate-500 dark:text-zinc-400 font-bold">يرجى تحديث بيانات حسابك لتتمكن من الانضمام للدورات الحصرية.</p>
          </div>
          <Link to="/dashboard/settings" className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-rose-500/20 transition-all hover:-translate-y-0.5 shrink-0">
            إكمال الإعدادات <Settings className="w-5 h-5" />
          </Link>
        </div>
      )}

      {/* ── Content Grid ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Latest Enrolled Courses */}
        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
          <div className="p-6 pb-4 flex items-center justify-between border-b border-slate-100 dark:border-white/5">
            <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-3">
              <div className="p-2 bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 rounded-lg">
                <BookOpen className="w-5 h-5" />
              </div>
              إصداراتك الحصرية
            </h3>
            <Link to="/dashboard/courses" className="text-sm font-bold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1">
              سوق الدورات <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>

          <div className="divide-y divide-slate-50 dark:divide-white/5">
            {enrolledCourses.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-50 dark:bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-300 dark:text-zinc-700">
                  <Play className="w-8 h-8 ml-1" />
                </div>
                <p className="text-slate-400 dark:text-zinc-500 font-bold">لم تشترك في أي دورة بعد</p>
              </div>
            ) : (
              enrolledCourses.map(course => {
                const totalLessons = course.curriculum?.reduce((sum, unit) => sum + (unit.lessons?.length || 0), 0) || 1;
                const completedCount = course.enrollmentData?.completedLessons?.length || 0;
                const percent = Math.min(100, Math.round((completedCount / totalLessons) * 100));

                return (
                  <Link key={course.id} to={`/courses/${course.id}`} className="flex items-center gap-5 p-5 hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-all group">
                    <div className="w-20 h-14 rounded-xl overflow-hidden bg-slate-100 dark:bg-zinc-800 shrink-0 border border-slate-200 dark:border-white/10 relative">
                      {course.thumbnail ? (
                        <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-slate-300 dark:text-zinc-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Play className="w-5 h-5 text-white fill-current ml-0.5" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-slate-900 dark:text-white truncate group-hover:text-primary-600 transition-colors">{course.title}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex-1 h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-primary-500 transition-all duration-1000" style={{ width: `${percent}%` }} />
                        </div>
                        <span className="text-[11px] font-black text-slate-400 dark:text-zinc-500 shrink-0">{percent}%</span>
                      </div>
                    </div>
                    <ArrowLeft className="w-4 h-4 text-slate-300 dark:text-zinc-700 transform group-hover:-translate-x-1 transition-transform" />
                  </Link>
                );
              })
            )}
          </div>
        </div>

        {/* Pending & Activity */}
        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
          <div className="p-6 pb-4 flex items-center justify-between border-b border-slate-100 dark:border-white/5">
            <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-3">
              <div className="p-2 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-lg">
                <Clock className="w-5 h-5" />
              </div>
              طلبات قيد الانتظار
            </h3>
          </div>

          <div className="divide-y divide-slate-50 dark:divide-white/5">
            {pendingCourses.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-50 dark:bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-300 dark:text-zinc-700">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <p className="text-slate-400 dark:text-zinc-500 font-bold">لا توجد طلبات معلقة</p>
              </div>
            ) : (
              pendingCourses.map(course => (
                <div key={course.id} className="p-5 flex items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden bg-slate-100 dark:bg-zinc-800 shrink-0 border border-slate-200 dark:border-white/10 grayscale-[0.5]">
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 dark:text-white truncate">{course.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                      <span className="text-xs font-black text-amber-600 dark:text-amber-400">جاري معالجة طلبك...</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Recent Achievement / Motivation (Optional context) */}
          <div className="p-6 bg-slate-50/50 dark:bg-black/20 border-t border-slate-100 dark:border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <TrendingUp className="w-5 h-5" />
              </div>
              <p className="text-sm font-bold text-slate-600 dark:text-zinc-400 leading-relaxed">
                أكمل <span className="text-slate-900 dark:text-white">3 دروس</span> أخرى هذا الأسبوع لتحافظ على ترتيبك في لوحة الصدارة!
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* ── Pending Assignments Section ────────────────────────── */}
      {pendingAssignments.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
          <div className="p-6 pb-4 flex items-center justify-between border-b border-slate-100 dark:border-white/5">
            <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-3">
              <div className="p-2 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-lg">
                <FileEdit className="w-5 h-5" />
              </div>
              ماذا ينتظر إبداعك؟
            </h3>
            <Link to="/dashboard/assignments" className="text-sm font-bold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1">
              عرض كل التكاليف <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>

          <div className="divide-y divide-slate-50 dark:divide-white/5">
            {pendingAssignments.slice(0, 3).map((item, idx) => (
              <div key={`${item.courseId}-${item.id}`} className="p-5 flex flex-col md:flex-row items-center gap-5 group">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border border-slate-100 dark:border-white/5 ${
                  item.type === 'final_project' ? 'bg-rose-50 dark:bg-rose-500/10' : 'bg-emerald-50 dark:bg-emerald-500/10'
                }`}>
                  {item.type === 'final_project' ? <Medal className="w-6 h-6 text-rose-500" /> : <FileEdit className="w-6 h-6 text-emerald-500" />}
                </div>
                
                <div className="flex-1 min-w-0 text-center md:text-right">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                    <p className="font-black text-slate-900 dark:text-white truncate">{item.title}</p>
                    {item.submission?.status === 'needs_revision' && (
                      <span className="px-2 py-0.5 bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400 rounded-full text-[10px] font-black">مطلوب تعديل</span>
                    )}
                  </div>
                  <p className="text-xs font-bold text-slate-400 dark:text-zinc-500 truncate">
                    {item.courseTitle} • {item.unitTitle}
                  </p>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                  <button
                    onClick={() => setSubmissionModalData({ ...item, sessionData: { ...item, type: item.type } })}
                    className="flex-1 md:flex-none px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white font-black rounded-xl text-sm transition-all shadow-lg shadow-primary-600/20 flex items-center justify-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    تسليم الآن
                  </button>
                  <Link
                    to={`/watch/${item.courseId}`}
                    className="p-2.5 bg-slate-50 dark:bg-white/5 text-slate-400 hover:text-primary-500 rounded-xl transition-colors border border-slate-100 dark:border-white/5"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          {pendingAssignments.length > 3 && (
            <div className="p-4 bg-slate-50/30 dark:bg-black/10 text-center">
              <Link to="/dashboard/assignments" className="text-xs font-black text-slate-500 dark:text-zinc-400 hover:text-primary-500 transition-colors uppercase tracking-wider">
                هناك {pendingAssignments.length - 3} تكاليف أخرى بانتظارك
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ── Request Course Action Card ────────────────────────────── */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 dark:from-zinc-900 dark:to-black border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden group shadow-xl mt-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-[80px] pointer-events-none group-hover:bg-primary-500/20 transition-colors duration-700"></div>
        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center shrink-0 border border-white/5 backdrop-blur-md">
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1 text-center md:text-right z-10">
          <h3 className="text-xl font-black text-white mb-1">لم تجد دورتك المفضلة؟</h3>
          <p className="text-slate-300 font-medium">نحن نستمع إليك! اطلب أي دورة غير متوفرة وسنعمل على توفيرها خصيصاً لك ولزملائك.</p>
        </div>
        <button
          onClick={() => setIsRequestModalOpen(true)}
          className="bg-primary-600 hover:bg-primary-500 text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-primary-600/20 transition-all hover:-translate-y-0.5 shrink-0 z-10"
        >
          <PlusCircle className="w-5 h-5" />
          طلب دورة غير متوفرة
        </button>
      </div>

      <RequestCourseModal isOpen={isRequestModalOpen} onClose={() => setIsRequestModalOpen(false)} />

      {/* ── Submission Modal ── */}
      <AnimatePresence>
        {submissionModalData && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSubmissionModalData(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden border border-white/10"
            >
              <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white">تسليم التكليف</h3>
                  <p className="text-sm font-bold text-slate-500 dark:text-zinc-500">{submissionModalData.title}</p>
                </div>
                <button
                  onClick={() => setSubmissionModalData(null)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-colors text-slate-400"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
                <AssignmentSubmission
                  sessionData={submissionModalData}
                  courseTitle={submissionModalData.courseTitle}
                  courseId={submissionModalData.courseId}
                  currentUser={currentUser}
                  onSuccess={() => {
                    setSubmissionModalData(null);
                    addToast('تم تسليم التكليف بنجاح!', 'success');
                    // Refresh data
                    window.location.reload();
                  }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
